"use client";

import { useState, useEffect } from "react";
import { siteConfig } from "@/lib/siteConfig";
import {
    Shield, Eye, User, Users, Database, Lock, Cookie,
    Scale, FileText, Phone, Mail, ArrowRight, CirclePercent,
    UserCheck, AlertTriangle, RefreshCw, BookOpen,
} from "lucide-react";
import MailtoLink from "@shared/MailtoLink";

/* ─── Table of contents ──────────────────────────────── */
const SECTIONS = [
    { id: "overview", title: "Overview", icon: BookOpen },
    { id: "purpose", title: "Purpose", icon: Eye },
    { id: "applicability", title: "Applicability", icon: Users },
    { id: "definitions", title: "Definitions", icon: FileText },
    { id: "gathering", title: "Gathering Personal Information", icon: Database },
    { id: "use", title: "Use of Personal Information", icon: UserCheck },
    { id: "security", title: "Securing Personal Information", icon: Lock },
    { id: "cookies", title: "How We Use Cookies", icon: Cookie },
    { id: "rights-access", title: "Rights to Access & Control", icon: Shield },
    { id: "rights-data", title: "Rights of Data Subjects", icon: Scale },
    { id: "exceptions", title: "Exceptions to Disclosure", icon: AlertTriangle },
    { id: "non-discrimination", title: "Non-Discrimination", icon: UserCheck },
    { id: "sale-bankruptcy", title: "Sale or Bankruptcy", icon: CirclePercent },
    { id: "revisions", title: "Revisions to Policy", icon: RefreshCw },
    { id: "contact", title: "Contact Us", icon: Phone },
];

/* ─── Helpers ────────────────────────────────────────── */
function SectionCard({ id, icon: Icon, title, children }) {
    return (
        <div
            id={id}
            className="scroll-mt-28 rounded-lg bg-white/70 backdrop-blur-xl border border-white/80 shadow-sm p-4 sm:p-7 md:p-7 lg:p-7"
        >
            <div className="flex items-center gap-3 mb-5">
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/10 border border-primary/15">
                    <Icon size={16} className="text-primary" />
                </span>
                <h2 className="text-[18px] font-semibold text-ink tracking-tight">{title}</h2>
            </div>
            <div className="flex flex-col gap-3 text-sm text-[#5B6478] leading-relaxed">
                {children}
            </div>
        </div>
    );
}

function SubHeading({ children }) {
    return (
        <p className="font-semibold text-ink text-sm mt-2 mb-0.5">{children}</p>
    );
}

function BulletList({ items }) {
    return (
        <ul className="flex flex-col gap-2 mt-1">
            {items.map((item, i) => (
                <li key={i} className="flex items-start gap-2.5">
                    <ArrowRight size={13} className="mt-0.5 shrink-0 text-primary" />
                    <span>{item}</span>
                </li>
            ))}
        </ul>
    );
}

/* ─── Page component ─────────────────────────────────── */
export default function Privacy() {
    const [activeSection, setActiveSection] = useState("overview");

    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) setActiveSection(entry.target.id);
                });
            },
            { rootMargin: "-20% 0px -70% 0px" }
        );
        SECTIONS.forEach(({ id }) => {
            const el = document.getElementById(id);
            if (el) observer.observe(el);
        });
        return () => observer.disconnect();
    }, []);

    const scrollTo = (id) =>
        document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });

    return (
        <>
            <section className="relative bg-[#f7f8fc] py-14 lg:py-20 px-0 md:px-4 lg:px-4 lg:px-0">
                <div className="absolute pointer-events-none top-0 right-0 w-[30rem] h-[30rem] rounded-full bg-[radial-gradient(circle,rgba(255,194,209,0.35)_0%,transparent_65%)] blur-3xl" />
                <div className="relative max-w-[1400px] mx-auto">
                    <div className="flex flex-col lg:flex-row gap-10 items-start">
                        <aside className="hidden lg:block w-64 xl:w-72 shrink-0 sticky top-30">
                            <div className="rounded-lg bg-white/70 backdrop-blur-xl border border-white/80 shadow-sm p-5">
                                <p className="font-semibold mb-2">
                                    Table of Contents
                                </p>
                                <nav className="flex flex-col gap-0.5">
                                    {SECTIONS.map(({ id, title, icon: Icon }) => (
                                        <button
                                            key={id}
                                            onClick={() => scrollTo(id)}
                                            className={`flex items-center gap-2.5 w-full text-left px-3 py-2 rounded-lg text-sm transition-all duration-150 cursor-pointer ${activeSection === id
                                                    ? "bg-primary/10 text-primary font-semibold"
                                                    : "text-ink/55 hover:text-ink hover:bg-black/[0.04]"
                                                }`}
                                        >
                                            <Icon size={14} className="shrink-0" />
                                            {title}
                                        </button>
                                    ))}
                                </nav>
                            </div>
                        </aside>

                        <div className="flex-1 min-w-0 flex flex-col gap-5 px-4 sm:px-0 md:px-0 lg:px-0">

                            <SectionCard id="overview" icon={BookOpen} title="Overview">
                                <p>Velan Info Services India Private Limited recognizes the importance of privacy and sensitivity to personal information. As handlers of data, we have a professional obligation to keep all classified information we receive from data subjects confidential.</p>
                                <p>Velan is committed to protecting any personal information/data that is collected, held, processed, or used by Velan during provision of our services or product. The Velan Privacy Policy outlines how we manage the personal information of employees, stakeholders (both internal &amp; external), and all personnel directly or indirectly involved.</p>
                                <p>Velan is responsible for the personal information we handle. To ensure our accountability, we have developed this Policy and trained our employees, contractors, agents, and all third parties about our policies and practices. We reserve the right to modify this Policy from time to time.</p>
                                <p>If we make any material changes, we will notify you by updating the Policy on our website prior to the change becoming effective. We encourage you to periodically review this page for the latest information on our privacy practices.</p>
                            </SectionCard>

                            <SectionCard id="purpose" icon={Eye} title="Purpose">
                                <p>This privacy policy explains how Velan uses and protects any information that you give when you use our website or when we ask you to provide certain information by which you can be identified. This is to assure that those data will only be used in accordance with this privacy statement.</p>
                            </SectionCard>

                            <SectionCard id="applicability" icon={Users} title="Applicability">
                                <p>This privacy policy is applicable for all customers, candidates seeking employment at Velan, employees, contractors, and suppliers whose personally identifiable information (PII) is collected, maintained, and/or processed by Velan.</p>
                                <p>The statutes and regulations applied are:</p>
                                <div className="flex flex-wrap gap-2 mt-1">
                                    {[
                                        { label: "GDPR", desc: "General Data Protection Regulation (EU)" },
                                        { label: "DPDP Act 2023", desc: "Digital Personal Data Protection Act, India" },
                                        { label: "CCPA", desc: "California Consumer Privacy Act" },
                                    ].map(({ label, desc }) => (
                                        <div key={label} className="flex items-start gap-2 px-3 py-2 rounded-lg bg-primary/8 border border-primary/15 w-full sm:w-auto">
                                            <Scale size={16} className="text-primary shrink-0" />
                                            <div>
                                                <p className="text-primary font-semibold text-[14px] leading-none">{label}</p>
                                                <p className="text-[12px] text-[#5B6478] mt-0.5">{desc}</p>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </SectionCard>

                            <SectionCard id="definitions" icon={FileText} title="Definitions">
                                <div className="flex flex-col gap-4">
                                    {[
                                        {
                                            term: "Data Subject",
                                            def: "An identified or identifiable natural person from whom or about whom information is collected in connection with our business and its operations.",
                                        },
                                        {
                                            term: "Personal Data",
                                            def: "Any information relating to an identified or identifiable natural person, including name, identification number, location data, online identifier, or factors specific to the physical, physiological, genetic, mental, economic, cultural or social identity of that person.",
                                        },
                                        {
                                            term: "Consent",
                                            def: "Any freely given, specific, informed, and unambiguous indication of the data subject's wishes signifying agreement to the processing of Personal Data. Silence, pre-ticked boxes, or inactivity shall not constitute consent.",
                                        },
                                        {
                                            term: "Data Processor",
                                            def: "A natural or legal person, public authority, agency, or other body which processes Personal Data on behalf of the Data Controller.",
                                        },
                                        {
                                            term: "Data Controller",
                                            def: "The natural or legal person, public authority, agency, or other body which determines the purposes and means of the processing of Personal Data.",
                                        },
                                    ].map(({ term, def }) => (
                                        <div key={term} className="flex gap-3">
                                            <div className="mt-1.5 w-2 h-2 rounded-full bg-primary shrink-0" />
                                            <div>
                                                <p className="font-semibold text-ink text-[14px] mb-0.5">{term}</p>
                                                <p className="leading-relaxed">{def}</p>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </SectionCard>

                            <SectionCard id="gathering" icon={Database} title="Gathering Personal Information">
                                <p>Velan may collect, maintain, and use your personal data that may have been provided to various departments including but not limited to Human Resources, Talent Acquisition, Finance, Marketing, Sales, and Travel.</p>

                                <SubHeading>What We Collect</SubHeading>
                                <p>Velan collects Personal Information through our website or applications, and in connection with business transactions, business partner relationships, and sales channel interactions. The types of personal information we collect may include:</p>
                                <BulletList items={[
                                    "Name, mailing address, city, state, zip, phone number, and email address",
                                    "Bill payment details including credit or debit card numbers, expiration date, and cardholder name",
                                    "Demographic information such as job title",
                                ]} />

                                <SubHeading>Personal Information from Third-Party Sources</SubHeading>
                                <p>If you interact with a third-party service to visit our sites (e.g., Facebook Connect, Google Sign-In, or Twitter), or if you share content from our website through a third-party social media service, the third-party service may send us information about you.</p>

                                <SubHeading>Personal Information from Our Website</SubHeading>
                                <p>Whenever you visit or interact with our sites, we may collect Usage Information including:</p>
                                <BulletList items={[
                                    "IP address or any other unique identifier assigned to your device",
                                    "Browser type, operating system, hardware, and mobile network information",
                                    "The URL that referred you to our sites",
                                    "Keywords searched which led you to our website",
                                    "Pages visited, links clicked, and time of visit",
                                ]} />
                            </SectionCard>

                            <SectionCard id="use" icon={UserCheck} title="Use of Personal Information">
                                <p>We use your personal information for everyday internal business purposes in order to provide our services, process your transactions, maintain your account(s), and provide customer service. Specifically:</p>
                                <BulletList items={[
                                    "Internal record keeping and improving our products and services",
                                    "Sending promotional emails about new products, special offers, or other information",
                                    "Market research — contacting you by email or phone",
                                    "Customizing the website according to your interests",
                                    "Sharing with banks, processors, and card networks to process transactions",
                                    "Cooperating in investigations of suspected fraud or illegal activity",
                                    "Complying with federal, state, and local laws or government regulations",
                                    "Contacting you and responding to your questions or requests",
                                ]} />
                                <p>Velan does not sell or rent any of your personal information, nor share it with third parties for marketing purposes without your prior permission.</p>
                            </SectionCard>

                            <SectionCard id="security" icon={Lock} title="Securing Personal Information">
                                <p>We are committed to ensuring that your information is secure. We have put in place suitable physical, electronic, and managerial procedures to safeguard and secure the information we collect. No method of transmission over the Internet is 100% secure; however, we strive to use commercially reasonable means to protect your personal information.</p>
                                <p>In the event that your sensitive personal information is accessed by an unauthorized individual and misuse is reasonably possible, we will notify you of the unauthorized access. Notification may be given by e-mail, writing, or phone at our sole discretion.</p>
                                <p>Velan enters into all necessary agreements including Data Protection agreements with relevant service providers to safeguard personal data.</p>
                                <div className="rounded-lg bg-primary/5 border border-primary/10 px-4 py-3 flex items-start gap-3 mt-1">
                                    <Mail size={16} className="text-primary mt-0.5 shrink-0" />
                                    <p>
                                        If you are concerned that your privacy may have been breached, please e-mail us at{" "}
                                        <MailtoLink email={siteConfig.emailInfo} className="text-primary underline-offset-2">{siteConfig.emailInfo}</MailtoLink>.
                                    </p>
                                </div>
                            </SectionCard>

                            <SectionCard id="cookies" icon={Cookie} title="How We Use Cookies">
                                <p>We use cookies to personalize content and to analyze our traffic. A cookie is a small file which asks permission to be placed on your computer&apos;s hard drive. Cookies allow web applications to respond to you as an individual by gathering and remembering information about your preferences.</p>
                                <p>We use traffic log cookies to identify which pages are being used. This helps us analyze data about web page traffic and improve our website in order to tailor it to customer needs. Data is used for statistical analysis purposes only.</p>
                                <p>You can choose to accept or decline cookies. Most web browsers automatically accept cookies, but you can modify your browser settings to decline cookies if you prefer. This may prevent you from taking full advantage of the website.</p>
                            </SectionCard>

                            <SectionCard id="rights-access" icon={Shield} title="Rights to Access and Control Your Personal Data">
                                <p>For Personal Data that we have about you:</p>
                                <div className="flex flex-col gap-3 mt-1">
                                    {[
                                        {
                                            title: "Delete Data",
                                            desc: "You can ask us to erase or delete all or some of your Personal Data. Once Velan confirms your verifiable request, we will delete your information from our records, unless an exception applies.",
                                        },
                                        {
                                            title: "Change or Correct Data",
                                            desc: "You can edit Personal Data through your account or ask us to change, update, or fix your data — particularly if it is inaccurate.",
                                        },
                                        {
                                            title: "Object or Restrict Use",
                                            desc: "You can ask us to stop using all or some of your Personal Data or to limit our use of it if it is inaccurate or unlawfully held.",
                                        },
                                        {
                                            title: "Right to Know",
                                            desc: "You have the right to know what personal information is collected, used, and shared — both as to the categories and specific pieces of personal information.",
                                        },
                                        {
                                            title: "Access and Portability",
                                            desc: "You can ask us for a copy of your Personal Data in machine readable form.",
                                        },
                                    ].map(({ title, desc }) => (
                                        <div key={title} className="rounded-lg bg-primary/5 border border-primary/10 p-4">
                                            <p className="font-semibold text-ink text-sm mb-1 flex items-center gap-2">
                                                <ArrowRight size={12} className="text-primary" /> {title}
                                            </p>
                                            <p>{desc}</p>
                                        </div>
                                    ))}
                                </div>
                            </SectionCard>

                            <SectionCard id="rights-data" icon={Scale} title="Rights of Data Subjects">
                                <p>Residents of the European Union and California are entitled to the following Data Protection rights:</p>
                                <BulletList items={[
                                    "Ask us to access, correct, update, or request deletion of Personal Information at any time",
                                    "Object to processing of Personal Information and ask us to restrict processing or request portability",
                                    'Opt-out of marketing communications by clicking the "unsubscribe" link in our emails',
                                    "Withdraw consent at any time — withdrawal will not affect prior lawful processing",
                                    "Complain to a Data Protection Authority (please notify Velan at least 36 hours prior)",
                                    "Velan will not sell the personal information of consumers of any age",
                                ]} />
                            </SectionCard>

                            <SectionCard id="exceptions" icon={AlertTriangle} title="Exceptions to Disclosure of Personal Information">
                                <p>Under certain circumstances, Velan will disclose personal information:</p>
                                <BulletList items={[
                                    "When required or authorized by law (e.g., court subpoena)",
                                    "When you have consented to the disclosure",
                                    "When the services we provide require us to share your information with third parties",
                                    "If we engage a third party for administrative services and they are bound by our Policy",
                                    "If the information is already publicly known",
                                ]} />
                            </SectionCard>

                            <SectionCard id="non-discrimination" icon={UserCheck} title="Non-Discrimination">
                                <p>Velan will not discriminate against any personnel for exercising any of their rights under GDPR, DPDPA, or CCPA. This includes denying goods or services, charging different prices, or providing a different level of quality.</p>
                            </SectionCard>

                            <SectionCard id="sale-bankruptcy" icon={CirclePercent} title="In the Event of Sale or Bankruptcy">
                                <p>The ownership of the Company may change at some point in the future. In the event of a sale, merger, public offering, or bankruptcy, your information may be shared with the person or business that owns or controls it. Opting out of receiving information from third parties will not affect our right to transfer your information to a new owner, but your choices will continue to be respected regarding the use of your information.</p>
                            </SectionCard>

                            <SectionCard id="revisions" icon={RefreshCw} title="Revisions to Privacy Policy">
                                <p>We reserve the right to change or supplement this Privacy Policy at any time. If we make any substantial changes (including with respect to how we use customer identifiable information) we will notify you by posting a prominent announcement on our website thirty (30) days prior to implementing the change, to the extent commercially possible.</p>
                                <p>Your continued use of the Velan websites following the posting of changes to these terms will mean that you accept those changes.</p>
                            </SectionCard>

                            <div id="contact" className="scroll-mt-28 rounded-lg bg-ink text-white overflow-hidden">
                                <div className="p-8">
                                    <h2 className="text-xl font-semibold mb-2">Contact Us</h2>
                                    <p className="text-white/60 text-sm mb-5">
                                        If you have any questions regarding our privacy policy, contact our customer service department.
                                    </p>

                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
                                        <div>
                                            <p className="font-semibold text-white/50 mb-3">
                                                General Enquiries
                                            </p>
                                            <div className="inline-flex flex-col gap-3">
                                                <MailtoLink email={siteConfig.emailInfo} className="flex items-center gap-2.5 text-sm text-white/85 hover:text-white transition-colors">
                                                    <Mail size={16} className="text-primary shrink-0" />
                                                    {siteConfig.emailInfo}
                                                </MailtoLink>
                                                <a href={`tel:${siteConfig.phoneTel}`} className="flex items-center gap-2.5 text-sm text-white/85 hover:text-white transition-colors">
                                                    <Phone size={16} className="text-primary shrink-0" />
                                                    {siteConfig.phone}
                                                </a>
                                                <a href={`tel:${siteConfig.phoneIndia1Tel}`} className="flex items-center gap-2.5 text-sm text-white/85 hover:text-white transition-colors">
                                                    <Phone size={16} className="text-primary shrink-0" />
                                                    {siteConfig.phoneIndia1}
                                                </a>
                                            </div>
                                        </div>

                                        <div>
                                            <p className="font-semibold text-white/50 mb-3">
                                                Data Protection Officer
                                            </p>
                                            <div className="inline-flex flex-col gap-2.5">
                                                <p className="flex items-center gap-2.5 text-sm text-white/85">
                                                    <User size={16} className="text-primary shrink-0" /> Pon Madasamy
                                                </p>
                                                <MailtoLink email={siteConfig.emailDpo} className="flex items-center gap-2.5 text-sm text-white/85 hover:text-white transition-colors">
                                                    <Mail size={16} className="text-primary shrink-0" />
                                                    {siteConfig.emailDpo}
                                                </MailtoLink>
                                                <a href={`tel:${siteConfig.phoneIndia2Tel}`} className="flex items-center gap-2.5 text-sm text-white/85 hover:text-white transition-colors">
                                                    <Phone size={16} className="text-primary shrink-0" />
                                                    {siteConfig.phoneIndia2}
                                                </a>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="h-1.5 bg-red-gradient" />
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </>
    );
}